/**
 * 加购-底部固定栏（Sticky Add to Cart Bar）
 * - 主产品区 `.product-form__buttons` 滚出视口后显示；回到该区域或到达 footer 时隐藏
 * - 加购：复用产品表单的加购按钮；Buy Now：构建购物车永久链接直达结账
 * - 变体切换时同步价格 / PC 图片 / 加购按钮状态
 *
 * 依赖产品主区的类选择器：.product-form__submit、.product-variant-id、
 * .velotric-product__price、.velotric-product__buy-buttons
 */
if (!customElements.get('product-sticky-bar')) {
  class ProductStickyBar extends HTMLElement {
    connectedCallback() {
      // 用类选择器定位产品主区元素（与项目原有逻辑一致，避免依赖 section id 匹配 DOM id）
      this.mainSubmit = document.querySelector('.product-form__submit');
      this.mainPriceEl = document.querySelector('.velotric-product__price');
      this.buyButtonsGroup =
        document.querySelector('.product-form__buttons') ||
        document.querySelector('.velotric-product__buy-buttons');
      this.footer = document.querySelector('.footer');

      this.buyButtonsVisible = false;
      this.buyButtonsPassed = false;
      this.footerReached = false;
      this.ticking = false;

      this.updateVisibility = this.updateVisibility.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.syncContent = this.syncContent.bind(this);
      this.onClick = this.onClick.bind(this);

      // 显隐：观察加购按钮组与 footer
      this.observer = new IntersectionObserver(this.onObserved.bind(this));
      if (this.buyButtonsGroup) this.observer.observe(this.buyButtonsGroup);
      if (this.footer) this.observer.observe(this.footer);

      window.addEventListener('scroll', this.onScroll, { passive: true });

      // 按钮事件（委托，兼容变体切换后的内容替换）
      this.addEventListener('click', this.onClick);

      // 内容同步：变体切换 + 主价格/加购按钮变化
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
      }
      if (this.mainPriceEl) {
        this.priceObserver = new MutationObserver(this.syncContent);
        this.priceObserver.observe(this.mainPriceEl, { childList: true, subtree: true, characterData: true });
      }
      if (this.mainSubmit) {
        this.submitObserver = new MutationObserver(this.syncContent);
        this.submitObserver.observe(this.mainSubmit, { attributes: true, attributeFilter: ['class', 'disabled'] });
      }

      this.updateVisibility();
      this.syncContent();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('click', this.onClick);
      this.observer?.disconnect();
      this.priceObserver?.disconnect();
      this.submitObserver?.disconnect();
      if (this.variantUnsubscribe) this.variantUnsubscribe();
    }

    onObserved(entries) {
      entries.forEach((entry) => {
        if (entry.target === this.buyButtonsGroup) {
          const rect = entry.boundingClientRect;
          this.buyButtonsVisible = entry.isIntersecting;
          this.buyButtonsPassed = rect.bottom < 0;
        }
        if (entry.target === this.footer) {
          this.footerReached = entry.isIntersecting;
        }
      });
      this.updateVisibility();
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.updateVisibility();
        this.ticking = false;
      });
    }

    updateVisibility() {
      // 主加购按钮组滚出视口上方后显示；回到该区域或 footer 进入视口时隐藏
      const visible = this.buyButtonsPassed && !this.buyButtonsVisible && !this.footerReached;
      this.classList.toggle('is-visible', visible);
    }

    onClick(event) {
      const atc = event.target.closest('[data-sticky-atc]');
      if (atc && this.contains(atc)) {
        if (this.mainSubmit && !this.mainSubmit.disabled) this.mainSubmit.click();
        return;
      }
      const buy = event.target.closest('[data-sticky-buy]');
      if (buy && this.contains(buy)) {
        this.buyNow();
      }
    }

    buyNow() {
      const variantInput =
        document.querySelector('.product-variant-id') ||
        document.querySelector('.product-form [name="id"]') ||
        document.querySelector('[name="id"]');
      const variantId = variantInput ? variantInput.value : null;
      if (!variantId) return;

      const qtyInput =
        document.querySelector('.product-form [name="quantity"]') || document.querySelector('[name="quantity"]');
      let quantity = parseInt(qtyInput && qtyInput.value, 10);
      if (!quantity || quantity < 1) quantity = 1;

      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      // 购物车永久链接：加入所选变体并直达结账
      window.location.href = cartUrl + '/' + variantId + ':' + quantity;
    }

    onVariantChange(event) {
      const data = event.data || event;
      const variant = data.variant;
      if (variant && variant.featured_image && variant.featured_image.src) {
        const src = this.addWidth(variant.featured_image.src, 200);
        this.querySelectorAll('[data-sticky-image] img').forEach((img) => {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = src;
          img.width = 200;
          const imageWidth = Number(variant.featured_image.width);
          const imageHeight = Number(variant.featured_image.height);
          img.height =
            imageWidth > 0 && imageHeight > 0
              ? Math.round((200 * imageHeight) / imageWidth)
              : 200;
        });
      }

      // 折扣区（折后价 / 节省金额）随变体切换更新：从重新渲染的分区 HTML 中取当前变体内容
      if (data.html) {
        const freshBar = data.html.querySelector('product-sticky-bar');
        if (freshBar) {
          const freshZones = freshBar.querySelectorAll('[data-sticky-discount]');
          this.querySelectorAll('[data-sticky-discount]').forEach((zone, index) => {
            const freshZone = freshZones[index];
            if (!freshZone) return;
            zone.classList.toggle(
              'velotric-sticky-bar__price-area--has-discount',
              freshZone.classList.contains('velotric-sticky-bar__price-area--has-discount')
            );
            zone.innerHTML = freshZone.innerHTML;
          });
        }
      }

      setTimeout(this.syncContent, 50);
    }

    addWidth(src, width) {
      if (!src) return src;
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', width);
        return url.toString();
      } catch (error) {
        const separator = src.indexOf('?') > -1 ? '&' : '?';
        return src + separator + 'width=' + width;
      }
    }

    syncContent() {
      // 价格：从主价格区复制现价
      const mainRegular = this.mainPriceEl && this.mainPriceEl.querySelector('.velotric-price-item--regular');
      if (mainRegular) {
        this.querySelectorAll('[data-sticky-price]').forEach((el) => {
          el.innerHTML = mainRegular.innerHTML;
        });
      }

      // 手机端划线价：从主价格区 compare_at 同步（仅 H5）
      const mainCompare = this.mainPriceEl && this.mainPriceEl.querySelector('.velotric-price__compare s');
      this.querySelectorAll('.velotric-sticky-bar__h5 [data-sticky-discount]').forEach((zone) => {
        const compareEl = zone.querySelector('[data-sticky-compare-at]');
        const compareText = mainCompare && mainCompare.textContent.trim();
        const onSale = !!compareText;
        zone.classList.toggle('velotric-sticky-bar__price-area--on-sale', onSale);
        if (compareEl) {
          compareEl.innerHTML = onSale ? `<s>${compareText}</s>` : '';
        }
      });

      // 加购按钮状态：禁用 / 文案 / loading
      if (this.mainSubmit) {
        const mainText = this.mainSubmit.querySelector('span');
        this.querySelectorAll('[data-sticky-atc]').forEach((btn) => {
          btn.disabled = this.mainSubmit.disabled;
          const textEl = btn.querySelector('.velotric-sticky-bar__btn-text');
          if (textEl && mainText) textEl.textContent = mainText.textContent.trim();
          btn.classList.toggle('loading', this.mainSubmit.classList.contains('loading'));
        });
      }
    }
  }

  customElements.define('product-sticky-bar', ProductStickyBar);
}
